import React, { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { Download, Copy, Check, Share2 } from "lucide-react";
import QRCode from "qrcode";

const loadImg = (src) => new Promise((res, rej) => {
  const im = new Image();
  im.onload = () => res(im);
  im.onerror = rej;
  im.src = src;
});

const FORMATS = [
  { key: "square", label: "Instagram Post", sub: "1080 × 1080", w: 1080, h: 1080, bg: "/ambassador-assets/square.jpg" },
  { key: "story", label: "Story / Reel", sub: "1080 × 1920", w: 1080, h: 1920, bg: "/ambassador-assets/story.jpg" },
  { key: "wide", label: "Web / Email Banner", sub: "1600 × 900", w: 1600, h: 900, bg: "/ambassador-assets/wide.jpg" },
];

const GOLD = "#D4AF37";
const INK = "#F5F5F0";
const MUT = "#A1A1AA";

// Ready-to-paste, RUO-compliant social captions (no human/animal-use claims).
const captionFor = (key, code, discount, link) => {
  const c = (code || "").toUpperCase();
  if (key === "story") {
    return `${discount}% off research-grade compounds at Molecularix Biolabs. Use code ${c} at checkout. For research use only. ${link}`;
  }
  if (key === "wide") {
    return `Molecularix Biolabs — reference-grade research compounds. Save ${discount}% with code ${c}. Strictly for research use only, not for human or veterinary use. ${link}`;
  }
  return `Premium reference-grade research compounds from Molecularix Biolabs. Strictly research use only. Use my code ${c} for ${discount}% off your order → ${link}`;
};

const roundRect = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
};

const setSpacing = (ctx, px) => { try { ctx.letterSpacing = `${px}px`; } catch (e) { /* older browsers */ } };

// Draws the circular Molecularix emblem in the center of a QR (kept small so scanning still works with H-level EC).
const drawCenterLogo = (ctx, cxc, cyc, qs, logoImg) => {
  if (!logoImg) return;
  const size = Math.round(qs * 0.24);
  const back = Math.round(size * 1.22);
  ctx.save();
  roundRect(ctx, cxc - back / 2, cyc - back / 2, back, back, Math.round(back * 0.24));
  ctx.fillStyle = "#F4EFE1";
  ctx.fill();
  ctx.beginPath();
  ctx.arc(cxc, cyc, size / 2, 0, Math.PI * 2);
  ctx.closePath();
  ctx.clip();
  ctx.drawImage(logoImg, cxc - size / 2, cyc - size / 2, size, size);
  ctx.restore();
};

// Word-wrap `text` to fit `maxWidth` at the current ctx font; caps at `maxLines`.
const wrapText = (ctx, text, maxWidth, maxLines) => {
  const words = text.split(/\s+/).filter(Boolean);
  const lines = [];
  let cur = "";
  for (const wd of words) {
    const test = cur ? `${cur} ${wd}` : wd;
    if (ctx.measureText(test).width > maxWidth && cur) {
      lines.push(cur);
      cur = wd;
    } else {
      cur = test;
    }
  }
  if (cur) lines.push(cur);
  return lines.slice(0, maxLines);
};

// Draws the fully composed, personalized graphic onto a canvas.
const drawGraphic = (canvas, fmt, img, { code, discount, url, headline, qrImg, logoImg }) => {
  const { w, h, key } = fmt;
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.clearRect(0, 0, w, h);

  // Background (cover fit)
  const scale = Math.max(w / img.width, h / img.height);
  const iw = img.width * scale;
  const ih = img.height * scale;
  ctx.drawImage(img, (w - iw) / 2, (h - ih) / 2, iw, ih);

  // Legibility overlay
  const grad = ctx.createLinearGradient(0, 0, 0, h);
  grad.addColorStop(0, "rgba(5,5,5,0.45)");
  grad.addColorStop(0.55, "rgba(5,5,5,0.55)");
  grad.addColorStop(1, "rgba(5,5,5,0.82)");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);

  const isWide = key === "wide";
  const u = isWide ? h / 1080 : w / 1080; // unit scale
  const align = isWide ? "left" : "center";
  const cx = isWide ? Math.round(w * 0.09) : w / 2;
  ctx.textAlign = align;
  ctx.textBaseline = "alphabetic";

  // Vertical anchor: story sits lower/centered; square/wide start higher to leave room for the QR
  let y = key === "story" ? Math.round(h * 0.26) : isWide ? Math.round(h * 0.22) : Math.round(h * 0.17);

  // Eyebrow
  setSpacing(ctx, 6 * u);
  ctx.font = `${Math.round(26 * u)}px "Courier New", monospace`;
  ctx.fillStyle = GOLD;
  ctx.fillText("MOLECULARIX ELITE", cx, y);
  setSpacing(ctx, 0);
  y += Math.round(64 * u);

  // Headline (custom text if provided, else default two lines)
  ctx.font = `300 ${Math.round(74 * u)}px Georgia, "Times New Roman", serif`;
  ctx.fillStyle = INK;
  const maxHl = isWide ? Math.round(w * 0.52) : Math.round(w * 0.84);
  const hlLines = headline && headline.trim()
    ? wrapText(ctx, headline.trim(), maxHl, 3)
    : ["Premium Research", "Compounds"];
  const lineH = Math.round(82 * u);
  hlLines.forEach((ln, idx) => {
    ctx.fillText(ln, cx, y);
    if (idx < hlLines.length - 1) y += lineH;
  });
  y += Math.round(44 * u);

  // Gold divider
  const dw = Math.round(90 * u);
  const dx = isWide ? cx : cx - dw / 2;
  ctx.fillStyle = GOLD;
  ctx.fillRect(dx, y, dw, Math.max(2, Math.round(3 * u)));
  y += Math.round(56 * u);

  // Subline
  ctx.font = `${Math.round(30 * u)}px Georgia, serif`;
  ctx.fillStyle = MUT;
  ctx.fillText(`Your exclusive ${discount}% ambassador discount`, cx, y);
  y += Math.round(56 * u);

  // Code panel
  const pw = Math.round(560 * u);
  const ph = Math.round(180 * u);
  const px = isWide ? cx : cx - pw / 2;
  ctx.save();
  roundRect(ctx, px, y, pw, ph, Math.round(10 * u));
  ctx.fillStyle = "rgba(212,175,55,0.08)";
  ctx.fill();
  ctx.lineWidth = Math.max(1.5, Math.round(2.5 * u));
  ctx.strokeStyle = GOLD;
  ctx.stroke();
  ctx.restore();

  const pcx = px + pw / 2; // panel content always centered within the panel
  ctx.textAlign = "center";
  setSpacing(ctx, 4 * u);
  ctx.font = `${Math.round(22 * u)}px "Courier New", monospace`;
  ctx.fillStyle = MUT;
  ctx.fillText("USE CODE AT CHECKOUT", pcx, y + Math.round(52 * u));
  setSpacing(ctx, 2 * u);
  ctx.font = `700 ${Math.round(66 * u)}px "Courier New", monospace`;
  ctx.fillStyle = GOLD;
  ctx.fillText((code || "").toUpperCase(), pcx, y + Math.round(128 * u));
  setSpacing(ctx, 0);
  ctx.textAlign = align;
  y += ph + Math.round(56 * u);

  // Referral URL
  ctx.font = `${Math.round(26 * u)}px "Courier New", monospace`;
  ctx.fillStyle = INK;
  ctx.fillText(url, cx, y);
  y += Math.round(30 * u);

  // Scannable QR code (a "live link" on the image — opens the store with the discount applied)
  if (qrImg) {
    const qs = Math.round((isWide ? 150 : key === "square" ? 140 : 170) * u); // QR pixel size
    const pad = Math.round(14 * u);
    const panel = qs + pad * 2;
    if (isWide) {
      // right side, vertically centered next to the left-aligned text block
      const qx = Math.round(w - panel - w * 0.10);
      const qy = Math.round((h - panel) / 2);
      ctx.save();
      roundRect(ctx, qx, qy, panel, panel, Math.round(10 * u));
      ctx.fillStyle = "#F4EFE1"; ctx.fill(); ctx.restore();
      ctx.drawImage(qrImg, qx + pad, qy + pad, qs, qs);
      drawCenterLogo(ctx, qx + pad + qs / 2, qy + pad + qs / 2, qs, logoImg);
      setSpacing(ctx, 3 * u);
      ctx.textAlign = "center";
      ctx.font = `${Math.round(17 * u)}px "Courier New", monospace`;
      ctx.fillStyle = GOLD;
      ctx.fillText("SCAN TO SHOP", qx + panel / 2, qy + panel + Math.round(30 * u));
      ctx.fillStyle = MUT;
      ctx.font = `${Math.round(14 * u)}px "Courier New", monospace`;
      ctx.fillText("DISCOUNT APPLIES AUTOMATICALLY", qx + panel / 2, qy + panel + Math.round(52 * u));
      setSpacing(ctx, 0);
    } else {
      // stacked: label above the QR so it never collides with the pinned footer
      ctx.textAlign = "center";
      setSpacing(ctx, 3 * u);
      ctx.font = `${Math.round(18 * u)}px "Courier New", monospace`;
      ctx.fillStyle = GOLD;
      ctx.fillText("SCAN TO SHOP · DISCOUNT AUTO-APPLIES", cx, y + Math.round(6 * u));
      setSpacing(ctx, 0);
      const qy = y + Math.round(22 * u);
      const qx = Math.round(cx - panel / 2);
      ctx.save();
      roundRect(ctx, qx, qy, panel, panel, Math.round(10 * u));
      ctx.fillStyle = "#F4EFE1"; ctx.fill(); ctx.restore();
      ctx.drawImage(qrImg, qx + pad, qy + pad, qs, qs);
      drawCenterLogo(ctx, qx + pad + qs / 2, qy + pad + qs / 2, qs, logoImg);
    }
    ctx.textAlign = align;
  }

  // RUO footer (pinned near bottom)
  ctx.textAlign = align;
  setSpacing(ctx, 3 * u);
  ctx.font = `${Math.round(20 * u)}px "Courier New", monospace`;
  ctx.fillStyle = "#71717A";
  ctx.fillText("FOR RESEARCH USE ONLY  ·  NOT FOR HUMAN CONSUMPTION", cx, h - Math.round(56 * u));
  setSpacing(ctx, 0);
};

// Platform-optimized, RUO-compliant caption templates for the vault.
const platformCaptions = (code, discount, link) => {
  const c = (code || "").toUpperCase();
  return [
    { key: "x", label: "X / Twitter",
      text: `Research-grade compounds, >99% purity verified with published batch records. Code ${c} = ${discount}% off at Molecularix Biolabs. For research use only — not for human consumption. ${link}` },
    { key: "ig", label: "Instagram",
      text: `Reference-grade research compounds from Molecularix Biolabs.\n\nThird-party tested · >99% purity verified · lot-verifiable COAs\nUse my code ${c} for ${discount}% off — link in bio.\n\nStrictly for laboratory research use only. Not for human consumption.\n#researchcompounds #peptideresearch #laboratorysupplies` },
    { key: "tg", label: "Telegram",
      text: `Molecularix Biolabs — premium research compounds, third-party tested with database-backed batch records.\n\n• ${discount}% off with code ${c}\n• Discount auto-applies via my link: ${link}\n• Free US shipping over $150\n\nFor research use only. Not for human consumption.` },
  ];
};

const VAULT_FILES = [
  { name: "Official Logo", sub: "Full emblem · PNG", file: "/brand/molecularix-logo.png", dl: "molecularix-logo.png" },
  { name: "Owl Mark", sub: "Compact avatar mark", file: "/brand/owl-mark.jpg", dl: "molecularix-owl-mark.jpg" },
  { name: "Hero 3D Render", sub: "High-res product scene", file: "/brand/hero.jpg", dl: "molecularix-hero-render.jpg" },
  { name: "Square 3D Render", sub: "1080 × 1080 background", file: "/ambassador-assets/square.jpg", dl: "molecularix-render-square.jpg" },
  { name: "Story 3D Render", sub: "1080 × 1920 background", file: "/ambassador-assets/story.jpg", dl: "molecularix-render-story.jpg" },
  { name: "Wide 3D Render", sub: "1600 × 900 background", file: "/ambassador-assets/wide.jpg", dl: "molecularix-render-wide.jpg" },
];

export default function AmbassadorAssets({ code, discount }) {
  const canvasRefs = useRef({});
  const [previews, setPreviews] = useState({});
  const [copied, setCopied] = useState(false);
  const [copiedCap, setCopiedCap] = useState(null);
  const [copiedPlat, setCopiedPlat] = useState(null);
  const [headline, setHeadline] = useState("");

  const copyPlatform = (pc) => {
    navigator.clipboard?.writeText(pc.text);
    setCopiedPlat(pc.key);
    toast.success(`${pc.label} caption copied`);
    setTimeout(() => setCopiedPlat((k) => (k === pc.key ? null : k)), 1800);
  };

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const refLink = `${origin}/?ref=${(code || "").toUpperCase()}`;
  const urlText = `${origin.replace(/^https?:\/\//, "")}/?ref=${(code || "").toUpperCase()}`;

  useEffect(() => {
    if (!code) return;
    let alive = true;
    (async () => {
      let qrImg = null;
      let logoImg = null;
      try {
        const qrUrl = await QRCode.toDataURL(refLink, { margin: 1, width: 460, errorCorrectionLevel: "H", color: { dark: "#0A0A0A", light: "#F4EFE1" } });
        qrImg = await loadImg(qrUrl);
      } catch (e) { qrImg = null; }
      logoImg = await loadImg("/brand/owl-mark.jpg").catch(() => null);
      for (const fmt of FORMATS) {
        if (!alive) return;
        const bg = await loadImg(fmt.bg).catch(() => null);
        const canvas = canvasRefs.current[fmt.key];
        if (!bg || !canvas) continue;
        drawGraphic(canvas, fmt, bg, { code, discount, url: urlText, headline, qrImg, logoImg });
        setPreviews((p) => ({ ...p, [fmt.key]: canvas.toDataURL("image/jpeg", 0.72) }));
      }
    })();
    return () => { alive = false; };
  }, [code, discount, urlText, headline, refLink]);

  const download = useCallback((fmt) => {
    const canvas = canvasRefs.current[fmt.key];
    if (!canvas) return;
    canvas.toBlob((blob) => {
      if (!blob) { toast.error("Could not generate image"); return; }
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `molecularix-${(code || "").toLowerCase()}-${fmt.key}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(a.href), 2000);
      toast.success(`${fmt.label} downloaded`);
    }, "image/png");
  }, [code]);

  const copyLink = () => {
    navigator.clipboard?.writeText(refLink);
    setCopied(true);
    toast.success("Referral link copied");
    setTimeout(() => setCopied(false), 1800);
  };

  const copyCaption = (fmt) => {
    navigator.clipboard?.writeText(captionFor(fmt.key, code, discount, refLink));
    setCopiedCap(fmt.key);
    toast.success("Caption copied — ready to paste");
    setTimeout(() => setCopiedCap((k) => (k === fmt.key ? null : k)), 1800);
  };

  return (
    <div data-testid="amb-assets">
      {/* Shareable referral link */}
      <div className="border border-[#D4AF37]/30 bg-[#D4AF37]/[0.05] p-6 mb-6" data-testid="amb-ref-link-card">
        <div className="flex items-center gap-2 mb-2">
          <Share2 size={14} className="text-[#D4AF37]" />
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">Your shareable link · auto-applies {discount}% off</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <code className="font-mono text-sm md:text-base text-[#D4AF37] break-all flex-1 min-w-[200px]" data-testid="amb-ref-link">{refLink}</code>
          <button onClick={copyLink} data-testid="amb-copy-link"
            className="flex items-center gap-2 border border-[#D4AF37]/50 text-[#D4AF37] px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] hover:bg-[#D4AF37] hover:text-black transition-colors">
            {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? "Copied" : "Copy Link"}
          </button>
        </div>
        <p className="mt-3 font-mono text-[10px] text-[#52525B]">Anyone who opens this link gets your discount applied automatically at checkout.</p>
      </div>

      {/* Promo assets */}
      <h3 className="font-mono text-xs uppercase tracking-[0.25em] text-[#A1A1AA] mb-4">Promo Assets · your code is baked in</h3>

      {/* Custom headline */}
      <div className="border border-zinc-800 bg-[#0C0C0C] p-4 mb-5" data-testid="amb-headline-editor">
        <label className="block font-mono text-[10px] uppercase tracking-[0.15em] text-[#52525B] mb-1.5">Custom headline <span className="text-[#3F3F46]">(optional · appears on all 3 graphics)</span></label>
        <div className="flex flex-wrap items-center gap-3">
          <input value={headline} onChange={(e) => setHeadline(e.target.value.slice(0, 60))} maxLength={60}
            placeholder="Premium Research Compounds" data-testid="amb-headline-input"
            className="flex-1 min-w-[220px] bg-transparent border border-zinc-700 px-3 py-2.5 font-mono text-xs outline-none focus:border-[#D4AF37]" />
          {headline && (
            <button onClick={() => setHeadline("")} data-testid="amb-headline-reset"
              className="border border-zinc-700 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] text-[#A1A1AA] hover:text-[#D4AF37] hover:border-[#D4AF37]/50 transition-colors">Reset</button>
          )}
        </div>
        <p className="mt-2 font-mono text-[10px] text-[#52525B]">Previews update live — {60 - headline.length} characters left. Keep it research-use-only compliant.</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="amb-assets-grid">
        {FORMATS.map((fmt) => (
          <div key={fmt.key} className="border border-zinc-800 bg-[#0C0C0C] p-4" data-testid={`amb-asset-${fmt.key}`}>
            <div className="bg-black border border-zinc-900 mb-3 flex items-center justify-center overflow-hidden" style={{ aspectRatio: `${fmt.w} / ${fmt.h}`, maxHeight: 320 }}>
              {previews[fmt.key]
                ? <img src={previews[fmt.key]} alt={`${fmt.label} preview`} className="w-full h-full object-contain" data-testid={`amb-asset-preview-${fmt.key}`} />
                : <span className="font-mono text-[10px] text-[#52525B]">Generating…</span>}
            </div>
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="font-mono text-[11px] text-[#E8E8E3] uppercase tracking-[0.12em]">{fmt.label}</p>
                <p className="font-mono text-[10px] text-[#52525B]">{fmt.sub}</p>
              </div>
              <button onClick={() => download(fmt)} data-testid={`amb-download-${fmt.key}`}
                className="flex items-center gap-2 bg-[#D4AF37] text-black px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] hover:bg-[#C5A028] transition-colors">
                <Download size={13} /> PNG
              </button>
            </div>
            <div className="mt-3 border-t border-zinc-900 pt-3" data-testid={`amb-caption-${fmt.key}`}>
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <p className="font-mono text-[9px] uppercase tracking-[0.15em] text-[#52525B]">Ready-to-paste caption</p>
                <button onClick={() => copyCaption(fmt)} data-testid={`amb-copy-caption-${fmt.key}`}
                  className="flex items-center gap-1.5 border border-[#D4AF37]/40 text-[#D4AF37] px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.12em] hover:bg-[#D4AF37] hover:text-black transition-colors">
                  {copiedCap === fmt.key ? <Check size={11} /> : <Copy size={11} />} {copiedCap === fmt.key ? "Copied" : "Copy"}
                </button>
              </div>
              <p className="font-mono text-[10px] leading-relaxed text-[#A1A1AA]" data-testid={`amb-caption-text-${fmt.key}`}>
                {captionFor(fmt.key, code, discount, refLink)}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Brand & Asset Vault */}
      <div className="mt-12" data-testid="amb-brand-vault">
        <h3 className="font-mono text-xs uppercase tracking-[0.25em] text-[#A1A1AA] mb-1">Brand &amp; Asset Vault</h3>
        <p className="font-mono text-[10px] text-[#52525B] mb-5">Official downloads — logos, 3D renders, captions and the mandatory compliance guide.</p>

        <a href="/ambassador-assets/molecularix-brand-guidelines.pdf" download="molecularix-brand-guidelines.pdf"
          data-testid="amb-vault-pdf"
          className="flex items-center justify-between gap-3 border border-[#D4AF37]/50 bg-[#D4AF37]/[0.06] p-5 mb-5 hover:bg-[#D4AF37]/[0.12] transition-colors">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-[#D4AF37] mb-1">Brand &amp; Compliance Guidelines · PDF</p>
            <p className="font-mono text-[10px] text-[#A1A1AA]">Mandatory reading — RUO disclaimer rules, approved claims &amp; logo usage.</p>
          </div>
          <span className="flex items-center gap-2 bg-[#D4AF37] text-black px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] shrink-0">
            <Download size={13} /> PDF
          </span>
        </a>

        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6" data-testid="amb-vault-grid">
          {VAULT_FILES.map((f) => (
            <a key={f.file} href={f.file} download={f.dl} data-testid={`amb-vault-${f.dl}`}
              className="group border border-zinc-800 bg-[#0C0C0C] hover:border-[#D4AF37]/50 transition-colors">
              <div className="aspect-video bg-black overflow-hidden flex items-center justify-center">
                <img src={f.file} alt={f.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-3 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-[#E8E8E3] truncate">{f.name}</p>
                  <p className="font-mono text-[9px] text-[#52525B]">{f.sub}</p>
                </div>
                <Download size={14} className="text-[#D4AF37] shrink-0" />
              </div>
            </a>
          ))}
        </div>

        <div className="border border-zinc-800 bg-[#0C0C0C] p-5" data-testid="amb-vault-captions">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] mb-4">Post templates · personalized per platform</p>
          <div className="grid md:grid-cols-3 gap-4">
            {platformCaptions(code, discount, refLink).map((pc) => (
              <div key={pc.key} className="border border-zinc-900 p-4 flex flex-col" data-testid={`amb-vault-caption-${pc.key}`}>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#D4AF37]">{pc.label}</p>
                  <button onClick={() => copyPlatform(pc)} data-testid={`amb-vault-copy-${pc.key}`}
                    className="flex items-center gap-1.5 border border-[#D4AF37]/40 text-[#D4AF37] px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.12em] hover:bg-[#D4AF37] hover:text-black transition-colors">
                    {copiedPlat === pc.key ? <Check size={11} /> : <Copy size={11} />} {copiedPlat === pc.key ? "Copied" : "Copy"}
                  </button>
                </div>
                <p className="font-mono text-[10px] leading-relaxed text-[#A1A1AA] whitespace-pre-line flex-1">{pc.text}</p>
              </div>
            ))}
          </div>
          <p className="mt-4 font-mono text-[9px] text-[#52525B]">Every template already includes the mandatory RUO disclaimer — keep it in your final post.</p>
        </div>
      </div>

      {/* Hidden full-res canvases used for rendering + export */}
      <div style={{ position: "absolute", left: -99999, top: 0, pointerEvents: "none" }} aria-hidden="true">
        {FORMATS.map((fmt) => (
          <canvas key={fmt.key} ref={(el) => { canvasRefs.current[fmt.key] = el; }} width={fmt.w} height={fmt.h} />
        ))}
      </div>
    </div>
  );
}
